import { DEFAULT_FIELDS_MATCH_EMPTY_SEARCH_QUERY } from 'modules/workflowNodeEditors/components/fieldsMatchEditor/constants';
import { Node, NodeData, NodeType } from 'modules/workflows/types';
import { NodeConnector } from 'ui/workflows/flowchart';
import { WORKFLOW_ACTIONS, WORKFLOW_NODE_TYPES, WORKFLOW_TRIGGERS } from '../base';
import { NODE_TITLES, NODE_TYPES } from './nodeConstants';

/**
 * This is the per-node type default data.
 * This will appear in `nodeData` after being dragged onto the flowchart.
 * You can use graphQL to determine what values should be in each type.
 */
export const NODE_DATA_BY_TYPE: Record<NodeType, NodeData> = {
  [NODE_TYPES.AB_SPLIT]: {
    nodeClass: WORKFLOW_ACTIONS.AB_SPLIT,
    nodeSuperClass: WORKFLOW_NODE_TYPES.FILTER,
    splitGroups: [{ splitPercentage: 0.5 }, { splitPercentage: 0.5 }],
  },
  [NODE_TYPES.ADD_CONTACT]: {
    emailListId: null,
    nodeClass: WORKFLOW_ACTIONS.ADD_CONTACT,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.ADD_USER_TO_FB]: {
    nodeClass: WORKFLOW_ACTIONS.ADD_USER_TO_FB,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.ATTRIBUTE_SPLIT]: {
    nodeClass: WORKFLOW_ACTIONS.ATTRIBUTE_SPLIT,
    nodeSuperClass: WORKFLOW_NODE_TYPES.FILTER,
    splitValues: [
      { value: '', isDefault: false },
      { value: '', isDefault: true },
    ],
  },
  [NODE_TYPES.CALL_WEBHOOK]: {
    nodeClass: WORKFLOW_ACTIONS.CALL_WEBHOOK,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.CHANGE_CONTACT_FIELD]: {
    mergeNestedObjects: false,
    nodeClass: WORKFLOW_ACTIONS.CHANGE_CONTACT_FIELD,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.DELAY]: {
    nodeClass: WORKFLOW_ACTIONS.DELAY,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.HOLD]: {
    nodeClass: WORKFLOW_ACTIONS.HOLD,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.HOLD_UNTIL_REPLY]: {
    nodeClass: WORKFLOW_ACTIONS.HOLD_UNTIL_REPLY,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.FIELDS_MATCH]: {
    searchQuery: DEFAULT_FIELDS_MATCH_EMPTY_SEARCH_QUERY,
    matchTriggeringEventFields: false,
    nodeClass: WORKFLOW_ACTIONS.FIELDS_MATCH,
    nodeSuperClass: WORKFLOW_NODE_TYPES.FILTER,
  },
  [NODE_TYPES.MODIFY_CHANNEL_SUBSCRIPTION]: {
    subscribed: true,
    nodeClass: WORKFLOW_ACTIONS.MODIFY_CHANNEL_SUBSCRIPTION,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.MODIFY_MESSAGE_TYPE]: {
    subscribed: true,
    nodeClass: WORKFLOW_ACTIONS.MODIFY_MESSAGE_TYPE,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.REMOVE_CONTACT]: {
    nodeClass: WORKFLOW_ACTIONS.REMOVE_CONTACT,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.REMOVE_USER_FROM_FB]: {
    nodeClass: WORKFLOW_ACTIONS.REMOVE_USER_FROM_FB,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.SEND_EMAIL]: {
    nodeClass: WORKFLOW_ACTIONS.SEND_EMAIL,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.SEND_EMBEDDED]: {
    nodeClass: WORKFLOW_ACTIONS.SEND_EMBEDDED,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.SEND_IN_APP]: {
    nodeClass: WORKFLOW_ACTIONS.SEND_IN_APP,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.SEND_PUSH]: {
    nodeClass: WORKFLOW_ACTIONS.SEND_PUSH,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.SEND_SMS]: {
    nodeClass: WORKFLOW_ACTIONS.SEND_SMS,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.SEND_WEB_PUSH]: {
    nodeClass: WORKFLOW_ACTIONS.SEND_WEB_PUSH,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.SEND_WHATSAPP]: {
    nodeClass: WORKFLOW_ACTIONS.SEND_WHATSAPP,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.START]: {
    workflowLifetimeLimit:
      null /** Default value when a new Journey is created (note: This implies unlimitated) */,
    workflowSimultaneousLimit: 1 /** Default value when a new Journey is created */,
    nodeClass: WORKFLOW_TRIGGERS.RECEIVED_API,
    nodeSuperClass: WORKFLOW_NODE_TYPES.TRIGGER,
  },
  [NODE_TYPES.TRIGGER_WORKFLOW]: {
    nodeClass: WORKFLOW_ACTIONS.TRIGGER_WORKFLOW,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.UPDATE_CONTACT_WEBHOOK]: {
    mergeNestedObjects: false,
    nodeClass: WORKFLOW_ACTIONS.UPDATE_CONTACT_WEBHOOK,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
  [NODE_TYPES.CHANNEL_OPT]: {
    nodeClass: WORKFLOW_ACTIONS.CHANNEL_OPT,
    nodeSuperClass: WORKFLOW_NODE_TYPES.ACTION,
  },
};

export const OUTPUT_CONNECTORS_BY_TYPE: Record<NodeType, NodeConnector[]> = Object.values(
  NODE_TYPES,
).reduce((acc, type) => {
  let outputConnectors;

  switch (type) {
    case NODE_TYPES.AB_SPLIT:
      outputConnectors = [{ name: '50%' }, { name: '50%' }];
      break;

    case NODE_TYPES.ATTRIBUTE_SPLIT:
      outputConnectors = [{ name: '-' }, { name: '-' }];
      break;

    case NODE_TYPES.FIELDS_MATCH:
      outputConnectors = [{ name: 'Yes' }, { name: 'No' }];
      break;

    case NODE_TYPES.HOLD:
      outputConnectors = [{ name: `\u2714 Rules met` }, { name: `\u2718 Rules not met` }];
      break;

    case NODE_TYPES.HOLD_UNTIL_REPLY:
      outputConnectors = [{ name: 'Other reply' }, { name: 'No reply' }];
      break;

    default:
      outputConnectors = [{ name: '' }];
      break;
  }

  return { ...acc, [type]: outputConnectors };
}, {});

const getBaseWFNode = (nodeType: NodeType) => ({
  nodeData: NODE_DATA_BY_TYPE[nodeType],
  // start node should not have any incoming connectors
  inputConnectors: nodeType === NODE_TYPES.START ? [] : [{ name: '' }],
  outputConnectors: OUTPUT_CONNECTORS_BY_TYPE[nodeType],
});

const getBaseNoteNode = () => ({
  body: '',
  // If the title is undefined, the note throws an error on save
  title: '',
});

/**
 * This contains the configurations for just the base node data
 * It should mirror the data in `nodes`, retrieved from the backend
 */
export const NODE_STRUCTURE_BY_TYPE: Record<NodeType, Omit<Node, 'id'>> = Object.values(
  NODE_TYPES,
).reduce(
  (acc: Record<NodeType, Omit<Node, 'id'>>, nodeType) => ({
    ...acc,
    [nodeType]: {
      type: nodeType,
      defaultTitle: NODE_TITLES[nodeType],
      isNote: nodeType === NODE_TYPES.NOTE,
      ...(nodeType === NODE_TYPES.NOTE ? getBaseNoteNode() : getBaseWFNode(nodeType)),
    },
  }),
  {},
);
